import { useRef, useState, useCallback, useEffect } from 'react';
import { saveVersionCanvas } from '../utils/canvasApi';
import { extractCanvasSnapshot } from '../utils/canvasSerializer';

const DEBOUNCE_MS = 2500;

/**
 * @param {Object} opts
 * @param {string}          opts.projectId
 * @param {string}          opts.definitionId
 * @param {number}          opts.versionNumber
 * @param {string}          opts.versionStatus    — "draft" | "published" | "archived"
 * @param {React.RefObject} opts.cyRef
 * @param {Array}           opts.layerNodes
 * @param {Array}           opts.layerEdges
 * @param {Set}             opts.collapsedLayers
 * @param {Object}          opts.nodeParentMapRef  — ref.current plain object
 * @returns {{ saveStatus, scheduleSave, triggerSave }}
 */
export function useCanvasAutosave({
  projectId,
  definitionId,
  versionNumber,
  versionStatus,
  cyRef,
  layerNodes,
  layerEdges,
  collapsedLayers,
  nodeParentMapRef,
  suggestedLayerIds = new Set(),
  collapsedNodePositions = {},
}) {
  const [saveStatus, setSaveStatus] = useState('idle');
  const debounceTimer = useRef(null);
  const isSaving      = useRef(false);
  const pendingSave   = useRef(false);

  const doSave = useCallback(async () => {
    if (!projectId || !definitionId || !versionNumber || !cyRef.current) return;
    if (versionStatus !== 'draft') return;   // never overwrite published or archived versions
    if (isSaving.current) { pendingSave.current = true; return; }

    const payload = extractCanvasSnapshot(
      cyRef.current, layerNodes, layerEdges, collapsedLayers, nodeParentMapRef, suggestedLayerIds, collapsedNodePositions,
    );
    if (!payload) return;

    isSaving.current = true;
    setSaveStatus('saving');
    try {
      await saveVersionCanvas(projectId, definitionId, versionNumber, payload);
      setSaveStatus('saved');
      if (pendingSave.current) {
        pendingSave.current = false;
        isSaving.current    = false;
        doSave();
      } else {
        isSaving.current = false;
      }
    } catch (err) {
      isSaving.current    = false;
      pendingSave.current = false;
      setSaveStatus('error');
      console.error('[CanvasAutosave] Save failed:', err);
    }
  }, [projectId, definitionId, versionNumber, versionStatus,
      cyRef, layerNodes, layerEdges, collapsedLayers, nodeParentMapRef, suggestedLayerIds, collapsedNodePositions]);

  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const t = setTimeout(() => setSaveStatus('idle'), 3000);
    return () => clearTimeout(t);
  }, [saveStatus]);

  const scheduleSave = useCallback(() => {
    if (!projectId || !definitionId || versionStatus !== 'draft') return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(doSave, DEBOUNCE_MS);
  }, [doSave, projectId, definitionId, versionStatus]);

  const triggerSave = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    doSave();
  }, [doSave]);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  return { saveStatus, scheduleSave, triggerSave };
}
